import { useMemo } from "react";

import { AnalyticsResourceV2Type } from "@intelligems/ig-types/schemas/analytics/base";
import { AnalyzeExperienceFrontendTinybirdRequestBodyType } from "@intelligems/ig-types/schemas/analytics/analyze-experience-tinybird";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import {
  TinybirdExperimentAnalysisInput,
  TransformedDataset,
} from "@src/api/analytics/types";
import { useBuildTinybirdPayload } from "@src/api/analytics/use-build-tinybird-payload";
import {
  normalizeTinybirdAnalyticsResponse,
  transformMetricsConfig,
  wrapAnalyticsRequest,
} from "@src/api/analytics/utils";
import { analyticsApi } from "@src/api/api";
import { dashboardKeys } from "@src/api/queryKeys";
import { useSelector } from "@src/store";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { QueryMeta } from "@src/types/react-query";
import { useQueries, UseQueryResult } from "@tanstack/react-query";

const fetchExperimentTinybirdAnalytics = async (
  data: AnalyzeExperienceFrontendTinybirdRequestBodyType
) =>
  wrapAnalyticsRequest(async () => {
    const resp = await analyticsApi.post<
      AnalyzeTinybirdResourceResponseBodyType,
      AnalyzeExperienceFrontendTinybirdRequestBodyType
    >(`/v2/analytics/analyze-resource-tinybird`, data);
    return normalizeTinybirdAnalyticsResponse(resp.data);
  });

export const useGetTinybirdExperienceAnalysis = (
  params: TinybirdExperimentAnalysisInput,
  resourceType: AnalyticsResourceV2Type
): UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[] => {
  const buildTinybirdPayload = useBuildTinybirdPayload();
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const transformedDataSets: TransformedDataset[] = transformMetricsConfig(
    params.graphConfigs
  );

  const hasUnresolvedProducts = dashboardFilters.selectedAnalyticsProducts.some(
    (product) => !product.handle
  );

  const result = useQueries({
    queries: transformedDataSets.map((datasetGroup) => {
      // Guard: Don't build payload if experience is missing
      // Query is disabled anyway, use stable placeholder key
      if (!params.experience) {
        return {
          queryKey: ["disabled-tinybird-query", datasetGroup.datasetId],
          queryFn: async () => {
            throw new Error("Experience required for analytics query");
          },
          enabled: false,
        };
      }

      const data = buildTinybirdPayload.build(
        params,
        datasetGroup,
        resourceType
      );
      return {
        queryKey: [dashboardKeys.selectedTinybirdExperiment(data)],
        meta: {
          onError: {
            "400": "ignore",
            "503": "ignore",
          },
        } as QueryMeta,
        queryFn: async () => fetchExperimentTinybirdAnalytics(data),
        enabled:
          params.enabled !== false &&
          !!data &&
          !hasUnresolvedProducts &&
          dashboardFilters.hiddenEndTime !== null &&
          dashboardFilters.experienceId === params.experience.id &&
          !(
            data.startTs &&
            data.endTs &&
            Number(data.startTs) >= Number(data.endTs)
          ) &&
          params.overviewDataLoaded,
        ...(params.cacheOptions && {
          staleTime: params.cacheOptions.staleTime,
          gcTime: params.cacheOptions.gcTime,
          refetchOnWindowFocus: params.cacheOptions.refetchOnWindowFocus,
          refetchOnReconnect: params.cacheOptions.refetchOnReconnect,
        }),
      };
    }),
  });
  // React-queries useQuery returns a new object always, so if you pass
  // this object down the component hierarchy you won't be able to prevent
  // renders. So if we want to pass the whole object we need to be sure
  // the object doesn't change if it's key data components don't change
  return useMemo(
    () => result,
    // eslint-disable-next-line react-compiler/react-compiler
    [
      ...result.flatMap((query) => [
        query.data,
        query.isSuccess,
        query.isFetching,
        query.isError,
        query.isPending,
      ]),
    ]
  );
};
